import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { ArrowRight } from 'lucide-react';

const products = [
  {
    name: 'Enrich IQ',
    label: 'Intelligence',
    tagline: 'Every Lead, a Complete Dossier',
    body:
      'Not a contact card. A full intelligence package — the kind your SDRs would build manually in 15 minutes, ready in seconds. Tech stack, real-time intent, verified contacts, buying committee, engagement playbook, personalised fit.',
    image:
      'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1600&q=80',
    href: '/products/enrich-iq',
    route: true,
    accent: 'from-brand-blue/70 via-navy-deep/60 to-navy-deep/95',
  },
  {
    name: 'AALIGN',
    label: 'Lead Management',
    tagline: 'Capture · Nurture · Convert',
    body:
      'The AI-powered lead management platform we license to publishers. AI lead scoring, real-time analytics, automated workflows, advanced reporting, and team collaboration — built on enterprise-grade security (GDPR, CCPA, SOC 2 Type II).',
    image:
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80',
    href: '/products/aalign',
    route: true,
    accent: 'from-brand-pink/60 via-navy-deep/60 to-navy-deep/95',
  },
  {
    name: 'Audience Intelligence',
    label: 'Account Briefings',
    tagline: 'Decision Architecture · Sizing',
    body:
      "Publisher-branded corporate intelligence briefings that map named decision-makers, size material contract renewals, chart the competitive set, and hand the advertiser's sales team a dated path to close.",
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80',
    href: '/products/audience-intelligence',
    route: true,
    accent: 'from-teal-500/50 via-navy-deep/60 to-navy-deep/95',
  },
];

const ProductCard = ({ p }) => (
  <div
    className="relative rounded-2xl overflow-hidden shadow-mk-lg hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300 min-h-[440px] flex flex-col justify-end"
    style={{
      backgroundImage: `url('${p.image}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}
  >
    <div className={`absolute inset-0 bg-gradient-to-b ${p.accent} pointer-events-none`} />
    <div className="relative p-7 sm:p-8 text-white">
      <span className="inline-block text-brand-blue-light text-[11px] font-bold uppercase tracking-[0.16em] mb-3">
        {p.label}
      </span>
      <h3 className="text-white font-black tracking-tight text-[26px] leading-tight">
        {p.name}
      </h3>
      <p className="mt-2 text-white/85 text-[13.5px] font-bold uppercase tracking-[0.06em]">
        {p.tagline}
      </p>
      <p className="mt-4 text-white/75 text-[14px] leading-[1.7]">{p.body}</p>
      {p.route ? (
        <Link
          to={p.href}
          className="mt-6 inline-flex items-center gap-1.5 text-white text-[12px] font-bold uppercase tracking-[0.1em] hover:gap-2.5 transition-all duration-300"
        >
          Get started
          <ArrowRight size={14} />
        </Link>
      ) : (
        <a
          href={p.href}
          className="mt-6 inline-flex items-center gap-1.5 text-white text-[12px] font-bold uppercase tracking-[0.1em] hover:gap-2.5 transition-all duration-300"
        >
          Get started
          <ArrowRight size={14} />
        </a>
      )}
    </div>
  </div>
);

const Products = () => {
  return (
    <section id="products" className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Centered header */}
        <div className="text-center max-w-3xl mx-auto mb-4">
          <p
            className="text-[13px] font-bold uppercase tracking-[0.18em] mb-3"
            style={{
              backgroundImage: 'linear-gradient(90deg, #1A73E8 0%, #4E9BF5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Products
          </p>
          <h2 className="text-navy font-black tracking-[-0.02em] leading-[1.05] text-[36px] sm:text-[44px] md:text-[52px]">
            Three products.<br />
            <span className="text-brand-blue">One engine.</span>
          </h2>
          <p className="mt-5 text-body-muted text-[16px] leading-[1.75]">
            Enrich IQ sources. AALIGN is the lead-management platform we license to publishers. Audience Intelligence produces the corporate briefings that turn a target account into a dated, sized, named path to close.
          </p>
        </div>

        {/* Colored background cards grid */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.name} delay={i * 130}>
              <ProductCard p={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
